import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import { getAllUsers, createUser, updateUser, deleteUser, User } from "@/api/users";

const empty = { email: "", password: "", is_admin: false };

export default function AdminUsers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const edit = searchParams.get("edit") || undefined;

  const [list, setList] = useState<User[]>([]);
  const [editing, setEditing] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const data = await getAllUsers();
      setList(data ?? []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!edit) {
      setEditing(null);
    } else if (edit === "new") {
      setEditing({ ...empty });
    } else if (list.length > 0) {
      const found = list.find(a => String(a.id) === edit);
      if (found) {
        setEditing({
          id: found.id,
          email: found.email,
          is_admin: found.is_admin,
          password: "",
        });
      }
    }
  }, [edit, list]);

  const save = async () => {
    const p = { ...editing };
    const isNew = !p.id;
    
    if (!p.email) {
      toast.error("E-posta adresi zorunludur.");
      return;
    }
    if (isNew && !p.password) {
      toast.error("Yeni kullanıcı için şifre zorunludur.");
      return;
    }

    try {
      setLoading(true);
      const apiPayload: any = {
        email: p.email,
        is_admin: p.is_admin,
      };
      if (p.password) {
        apiPayload.password = p.password;
      }

      if (isNew) {
        await createUser(apiPayload);
      } else {
        await updateUser(p.id, apiPayload);
      }
      toast.success("Kullanıcı kaydedildi");
      setSearchParams({});
      load();
    } catch (err: any) {
      toast.error("Hata: " + (err.response?.data?.error || err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const del = async (id: number) => {
    if (!confirm("Kullanıcıyı silmek istediğinize emin misiniz?")) return;
    try {
      await deleteUser(id);
      toast.success("Kullanıcı silindi");
      load();
    } catch (err: any) {
      toast.error("Hata: " + (err.response?.data?.error || err.message || err));
    }
  };

  return (
    <div>
      {editing ? (
        <div className="space-y-6">
          <div className="flex justify-between items-center border-b pb-4">
            <div className="space-y-1">
              <button
                onClick={() => setSearchParams({})}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
              >
                <ArrowLeft size={16} />
                Kullanıcı Listesine Dön
              </button>
              <h1 className="text-2xl font-bold text-primary">
                {editing.id ? "Kullanıcıyı Düzenle" : "Yeni Kullanıcı Ekle"}
              </h1>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSearchParams({})}
                className="px-4 py-2 text-sm font-semibold rounded-lg border bg-background hover:bg-muted cursor-pointer transition-colors"
              >
                İptal
              </button>
              <button
                onClick={save}
                disabled={loading}
                className="px-5 py-2 text-sm font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/95 cursor-pointer transition-colors shadow-sm disabled:opacity-50"
              >
                {loading ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>

          <div className="max-w-2xl">
            <div className="bg-card border rounded-xl p-6 shadow-2xs space-y-4">
              <Row label="E-posta Adresi">
                <input
                  type="email"
                  className="inp"
                  value={editing.email}
                  onChange={e => setEditing({ ...editing, email: e.target.value })}
                />
              </Row>

              <Row label={editing.id ? "Şifre (Değiştirmek istemiyorsanız boş bırakın)" : "Şifre"}>
                <input
                  type="password"
                  className="inp"
                  value={editing.password}
                  onChange={e => setEditing({ ...editing, password: e.target.value })}
                />
              </Row>

              <div className="border-t pt-4 mt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                    checked={editing.is_admin}
                    onChange={e => setEditing({ ...editing, is_admin: e.target.checked })}
                  />
                  <span className="text-sm font-semibold text-foreground">Yönetici (Admin) Yetkisi</span>
                </label>
                <p className="text-[11px] text-muted-foreground ml-6 mt-0.5">
                  Admin yetkisine sahip kullanıcılar tüm sisteme erişebilir.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-primary">Kullanıcılar</h1>
            <button
              onClick={() => setSearchParams({ edit: "new" })}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-md flex items-center gap-2 cursor-pointer hover:opacity-90 transition-opacity font-semibold text-sm shadow-sm"
            >
              <Plus size={16}/> Yeni Kullanıcı Ekle
            </button>
          </div>
          <div className="bg-card border rounded-xl overflow-hidden shadow-2xs">
            <table className="w-full text-sm">
              <thead className="bg-accent/30 text-muted-foreground font-semibold">
                <tr className="border-b">
                  <th className="text-left p-3.5">Kayıt Tarihi</th>
                  <th className="text-left p-3.5">E-posta</th>
                  <th className="text-left p-3.5">Yetki</th>
                  <th className="text-right p-3.5">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {list.map(a => (
                  <tr key={a.id} className="hover:bg-muted/30 transition-colors">
                    <td className="p-3.5 text-muted-foreground">
                      {a.created_at ? new Date(a.created_at).toLocaleDateString("tr-TR") : "-"}
                    </td>
                    <td className="p-3.5 font-medium">{a.email}</td>
                    <td className="p-3.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        a.is_admin ? "bg-purple-50 text-purple-700 border border-purple-200" : "bg-slate-100 text-slate-700 border border-slate-200"
                      }`}>
                        {a.is_admin ? "Admin" : "Kullanıcı"}
                      </span>
                    </td>
                    <td className="p-3.5 text-right">
                      <div className="flex items-center gap-1.5 justify-end">
                        <button
                          onClick={() => setSearchParams({ edit: String(a.id) })}
                          className="px-2.5 py-1 text-xs font-semibold rounded cursor-pointer transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100"
                        >
                          Düzenle
                        </button>
                        <button
                          onClick={() => del(a.id)}
                          className="p-1.5 hover:bg-destructive/10 text-destructive rounded cursor-pointer transition-colors ml-1"
                        >
                          <Trash2 size={16}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center p-8 text-muted-foreground">Henüz kullanıcı eklenmemiş.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <style>{`
        .inp {
          width: 100%;
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 10px 12px;
          background: var(--background);
          font-size: 0.875rem;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .inp:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.15);
        }
      `}</style>
    </div>
  );
}

function Row({ label, children, className = "", ...props }: any) {
  return (
    <div className={`flex flex-col ${className}`} {...props}>
      <label className="block text-sm font-medium mb-1 text-slate-700">{label}</label>
      {children}
    </div>
  );
}
